/**
 * Jobs — Sprint 2 / TASK 1-7 refactor.
 *
 * Presentation only. Server-side fetch, ranking, save logic, the
 * `useApplyClickLogger` indirection, JobDetailDrawer, and
 * ApplyConfirmationModal behaviour are all preserved verbatim. What
 * changes is the layout: the page now follows the Mobbin "workspace
 * home" pattern (Walkthrough/Deel web Jan 2026 12.png):
 *
 *   ┌──────────────────────────────────────────────────────────┐
 *   │ Header + tab strip + filter chips                         │
 *   ├────────────────────────────┬─────────────────────────────┤
 *   │ Left column                │ Right column                │
 *   │  • Recommended for you     │  • For you today (tasks)    │
 *   │  • Saved jobs              │                             │
 *   ├────────────────────────────┴─────────────────────────────┤
 *   │ All open roles (the existing dense JobCard grid)          │
 *   └──────────────────────────────────────────────────────────┘
 *
 * On `<md` viewports the two columns stack and chips wrap.
 */
import {
  AlertCircle,
  Bookmark,
  Briefcase,
  ChevronLeft,
  ChevronRight,
  ChevronRight as ChevronRightIcon,
  RefreshCw,
  Sparkles,
  TrendingUp,
  Video,
  X,
  Zap,
} from 'lucide-react';
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import LoadingSpinner from '../../../components/LoadingSpinner';
import { getCreditCost } from '../../../config/creditCosts';
import { supabase } from '../../../lib/supabase';
import { notifyCreditsChanged } from '../../../utils/creditsSync';
import { getTopJobsForUser } from '../../../modules/recommendations/engine/recommendationEngine';
import type { RecommendationResult } from '../../../modules/recommendations/types';
import { roleAlignmentScore } from '../../../modules/recommendations/engine/scoring/dimensionScores';
import { hasNaukriJobCredential } from '../../../services/externalJobCredentials';
import { refreshJobFeeds, userFacingRefreshMessage } from '../../../services/jobFeedService';
import {
  buildUnifiedRecommendedFeedCore,
  RECOMMENDED_POOL_LIMIT,
} from '../../../services/unifiedRecommendedFeedBuilder';
import JobWorkflowStatusBadge from '../../ai-agent/components/JobWorkflowStatusBadge';
import { useAppliedJobsMap, type AppliedJobInfo } from '../../ai-agent/hooks/useAppliedJobsMap';
import { useUserProfile } from '../hooks/useUserProfile';
import { useSavedJobs } from '../hooks/useSavedJobs';
import { useProfileSkills } from '../hooks/useProfileSkills';
import { useUserSkills } from '../hooks/useUserSkills';

import type { Job } from './JobCard';
import JobCard from './JobCard';
import ApplyConfirmationModal from './Jobs/ApplyConfirmationModal';
import { writeAgentOptimizeHandoff } from './Jobs/agentOptimizeHandoff';
import { cleanSkills, isJunkJob, OWNER_EMAIL } from './Jobs/helpers';
import {
  balanceChannelDisplayOrder,
  extractRoleTargets,
  filterBlatantLocationConflicts,
  hasGeoPreferences,
  sortJobsByPreferenceRelevance,
} from './Jobs/jobPreferenceFilter';
import HiddenOpportunities from './HiddenOpportunities';
import JobMatchExplainerModal from './JobMatchExplainerModal';
import { buildHiddenOpportunities, jobListToHiddenSources } from '../utils/buildHiddenOpportunities';
import JobDetailDrawer from './Jobs/JobDetailDrawer';
import { buildJobExplainerBreakdown } from '../utils/buildJobExplainerBreakdown';
import type { JobFiltersState } from './Jobs/JobFilters';
import JobFilters, {
  applyJobFilters,
  applyRecToolbarFilters,
  defaultFilters,
  defaultRecToolbarFilters,
  FRESHNESS_DAYS,
  hasActiveRecToolbarFilters,
  type RecToolbarFilters,
} from './Jobs/JobFilters';
import JobTableRow from './Jobs/JobTableRow';
import { rankJobs } from './Jobs/ranking';
import SavedJobsList from './Jobs/SavedJobsList';
import type { DashboardPage } from './Layout';

export { freshnessLabel } from './Jobs/helpers';

// ---------------------------------------------------------------------------
// Types — DB row shape preserved verbatim from the legacy implementation.
// ---------------------------------------------------------------------------

type PeerlistRow = {
  id: string;
  url: string;
  title: string;
  company: string;
  company_logo_url: string | null;
  location: string | null;
  remote: boolean | null;
  job_type: string | null;
  experience_level: string | null;
  salary_min: number | null;
  salary_max: number | null;
  salary_currency: string | null;
  skills: string[] | null;
  must_have_skills: string[] | null;
  good_to_have_skills: string[] | null;
  duration: string | null;
  compensation_text: string | null;
  description_md: string | null;
  posted_at: string | null;
  last_seen_at: string;
  external_slug: string;
  source_channel: string | null;
};

type Tab = 'all' | 'saved';

const DEFAULT_PAGE_SIZE = 20;
const PAGE_SIZE_OPTIONS = [20, 50, 100] as const;
const RECOMMENDED_TOP_N = 6;
/** Unified per-user pool in recommended_jobs (all channels). */
const REC_GRID_PAGE_SIZE = 20;
const JOB_FRESHNESS_DAYS = 90;

const PEERLIST_JOBS_SELECT =
  'id, url, title, company, company_logo_url, location, remote, ' +
  'job_type, experience_level, salary_min, salary_max, salary_currency, ' +
  'skills, must_have_skills, good_to_have_skills, duration, ' +
  'compensation_text, description_md, posted_at, last_seen_at, external_slug, source_channel';

interface JobsProps {
  /** Optional navigate handler — wires the "Review" task buttons. */
  onNavigate?: (page: DashboardPage) => void;
  /**
   * View mode:
   * - 'recommended' (default): Shows personalized recommended jobs for the user
   * - 'admin': Shows all jobs from all channels (admin Job Board view)
   */
  mode?: 'recommended' | 'admin';
}

// ---------------------------------------------------------------------------
// Helpers (unchanged)
// ---------------------------------------------------------------------------

function applyServerPeerlistFilters(
  q: ReturnType<ReturnType<typeof supabase.from>['select']>,
  f: JobFiltersState,
) {
  let query = q.eq('is_active', true);
  if (f.freshness !== 'any') {
    const days = FRESHNESS_DAYS[f.freshness];
    if (days != null) {
      const cutoffISO = new Date(
        Date.now() - days * 24 * 60 * 60 * 1000,
      ).toISOString();
      query = query.gte('posted_at', cutoffISO);
    }
  }
  if (f.remote === 'remote') {
    query = query.eq('remote', true);
  } else if (f.remote === 'onsite') {
    query = query.eq('remote', false);
  }
  return query;
}

function mapAndFilterRows(rows: unknown[] | null): Job[] {
  const list = (rows ?? []) as PeerlistRow[];
  return list
    .map((r) => toJob(r))
    .filter(
      (j) =>
        !isJunkJob({
          company: j.company,
          title: j.title,
          skills: j.skills,
          description: j.description,
        }),
    );
}

function toJob(r: PeerlistRow): Job {
  const skills = cleanSkills(r.skills);
  const mustHave = cleanSkills(r.must_have_skills);
  const goodToHave = cleanSkills(r.good_to_have_skills);
  const channel = r.source_channel?.toLowerCase() ?? 'peerlist';
  return {
    id: r.id,
    // source_channel drives the badge; 'peerlist' is only set when truly from Peerlist
    source: channel === 'peerlist' ? 'peerlist' : 'direct',
    source_channel: channel !== 'peerlist' ? channel : undefined,
    title: r.title,
    company: r.company,
    company_logo_url: r.company_logo_url ?? undefined,
    location: r.location ?? '',
    url: r.url,
    created: r.last_seen_at,
    posted_at: r.posted_at ?? undefined,
    description: r.description_md ?? undefined,
    skills: skills.length > 0 ? skills : undefined,
    must_have_skills: mustHave.length > 0 ? mustHave : undefined,
    good_to_have_skills: goodToHave.length > 0 ? goodToHave : undefined,
    duration: r.duration ?? undefined,
    compensation_text: r.compensation_text ?? undefined,
    salary_min: r.salary_min ?? undefined,
    salary_max: r.salary_max ?? undefined,
    salary_currency: r.salary_currency ?? 'INR',
    job_type: r.job_type ?? undefined,
    experience_level: r.experience_level ?? undefined,
    remote: r.remote ?? undefined,
    external_job_id: r.external_slug,
  };
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const Jobs: React.FC<JobsProps> = ({ onNavigate, mode = 'recommended' }) => {
  const { t, i18n } = useTranslation();
  const isAdminMode = mode === 'admin';
  const [activeTab, setActiveTab] = useState<Tab>('all');
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('grid');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [jobs, setJobs] = useState<Job[]>([]);
  const [filters, setFilters] = useState<JobFiltersState>(() => ({ ...defaultFilters }));
  const [isSignedIn, setIsSignedIn] = useState(false);
  const [authReady, setAuthReady] = useState(false);
  const [isOwner, setIsOwner] = useState(false);
  const [selectedJob, setSelectedJob] = useState<Job | null>(null);
  const [confirmationJob, setConfirmationJob] = useState<Job | null>(null);
  const [explainJob, setExplainJob] = useState<Job | null>(null);
  const [confirmedApplicationId, setConfirmedApplicationId] = useState<string | null>(null);
  const [fetchKey, setFetchKey] = useState(0);
  const [page, setPage] = useState(1);
  const [recPage, setRecPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);
  const [serverTotal, setServerTotal] = useState<number | null>(null);
  const [recJobs, setRecJobs] = useState<Job[]>([]);
  const [recJobsLoading, setRecJobsLoading] = useState(false);
  const fetchGenRef = useRef(0);
  const recFetchGenRef = useRef(0);
  const externalFetchingRef = useRef(false);
  const serverFilterKeyRef = useRef<string | null>(null);

  const retry = useCallback(() => setFetchKey((k) => k + 1), []);

  // Resume-based ranking — runs only when the user is signed in.
  const { ctx: resumeCtx, hasResume, loading: resumeSkillsLoading } = useUserSkills(isSignedIn);
  // Profile-skills ranking — uses user_profiles.skills (no resume upload needed).
  const {
    ctx: profileCtx,
    skills: profileSkillList,
    hasProfile,
    experienceYears,
    location: profileLocation,
    currentJobTitle: profileJobTitle,
    loading: profileSkillsLoading,
  } = useProfileSkills(isSignedIn);
  // Merge: prefer resume (richer titles) but supplement with profile skills
  const userCtx = useMemo(() => {
    if (!resumeCtx && !profileCtx) return null;
    const base = !resumeCtx ? profileCtx :
      !profileCtx ? resumeCtx :
      {
        skills: new Set([...resumeCtx.skills, ...profileCtx.skills]),
        titles: new Set([...resumeCtx.titles, ...profileCtx.titles]),
      };
    // Attach experience years (for experience-fit multiplier) and headline
    // (for cross-domain role alignment: PM vs Marketing, etc.)
    return base ? { ...base, experienceYears, headline: profileJobTitle } : null;
  }, [resumeCtx, profileCtx, experienceYears, profileJobTitle]);
  const hasSkillContext = hasResume || hasProfile;
  const skillContextReady = isSignedIn && !profileSkillsLoading && !resumeSkillsLoading;

  // DB-persisted recommendations — full objects so we can use Pinecone scores
  const [dbRecs,       setDbRecs]       = useState<RecommendationResult[]>([]);
  const [dbRecLoading, setDbRecLoading] = useState(false);
  const [recRefreshKey, setRecRefreshKey] = useState(0);
  // Convenience set for O(1) "is this job Pinecone-recommended?"
  const dbRecJobIds = useMemo(() => new Set(dbRecs.map(r => r.job_id)), [dbRecs]);
  const recByJobId = useMemo(
    () => new Map(dbRecs.map((r) => [r.job_id, r])),
    [dbRecs],
  );

  // FETCH Live — pulls jobs from live channels into DB + recommended_jobs (credit-costly).
  const [liveFetching, setLiveFetching] = useState(false);
  const [liveFetchOverlay, setLiveFetchOverlay] = useState(false);
  const [liveFetchToast, setLiveFetchToast] = useState<{ found: number; sources: string[]; detail?: string } | null>(null);

  const { lookup: lookupApplied } = useAppliedJobsMap();
  const { profile: userProfile } = useUserProfile();

  /**
   * FETCH Live — ingests from Peerlist, LinkedIn, Naukri, etc., merges new jobs into recommended_jobs.
   */
  const handleFetchLive = useCallback(async () => {
    if (externalFetchingRef.current) return;
    externalFetchingRef.current = true;
    setLiveFetchToast(null);
    setLiveFetchOverlay(true);
    setLiveFetching(true);

    const overlayCap = window.setTimeout(() => {
      externalFetchingRef.current = false;
      setLiveFetching(false);
    }, 30_000);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return;

      const keywords = profileJobTitle ?? 'software engineer';
      const result = await refreshJobFeeds({
        keywords,
        location: profileLocation ?? 'India',
        maxResults: 50,
        experienceYears,
        profileSkills: profileSkillList,
      });

      if (typeof result.credits_balance === 'number' && Number.isFinite(result.credits_balance)) {
        notifyCreditsChanged({ newBalance: result.credits_balance });
      } else if (result.insufficient_credits) {
        setLiveFetchToast({
          found: 0,
          sources: [],
          detail: t('dashboard.jobs.notEnoughCredits', {
            defaultValue: 'Not enough credits — FETCH Live costs {{credits}} credits.',
            credits: getCreditCost('job_feed_refresh'),
          }),
        });
        setTimeout(() => setLiveFetchToast(null), 8000);
        return;
      }

      const sources = result.external?.sources ?? [];
      const naukriInfo = result.external?.source_info?.naukri as { 
        found: number; 
        has_credentials: boolean;
        blocked?: boolean;
        note?: string;
      } | undefined;
      const naukriHint =
        // Check if Naukri is blocked (server-side bot detection)
        naukriInfo?.blocked
          ? t('dashboard.jobs.naukriBlocked', {
              defaultValue:
                'Naukri is blocking server-side requests (bot detection). Jobs come from LinkedIn & Peerlist instead.',
            })
          : hasNaukriJobCredential() && !sources.includes('naukri')
            ? t('dashboard.jobs.naukriFetchMiss', {
                defaultValue:
                  'Naukri returned no jobs — your session may be expired. Refresh nauk_at token in Settings.',
              })
            : naukriInfo?.has_credentials && naukriInfo.found === 0
              ? t('dashboard.jobs.naukriZeroResults', {
                  defaultValue:
                    'Naukri had no matches. This may be due to server-side blocking by Naukri.',
                })
              : null;
      const detail = !result.ok
        ? (userFacingRefreshMessage(result.message, false)
          ?? t('dashboard.jobs.fetchFailed', 'Refresh failed — try again in a moment.'))
        : result.new_jobs_count != null && result.new_jobs_count > 0
          ? t('dashboard.jobs.newJobsAdded', {
              defaultValue: '{{count}} new jobs added to your list.',
              count: result.new_jobs_count,
            })
          : result.batch_jobs_count != null && result.batch_jobs_count > 0
            ? t('dashboard.jobs.jobsRefreshed', {
                defaultValue: '{{count}} jobs refreshed — all already in your list.',
                count: result.batch_jobs_count,
              })
            : result.total > 0 && sources.length > 0
              ? t('dashboard.jobs.liveFetchStored', {
                  defaultValue: 'Stored {{count}} jobs from {{sources}}',
                  count: result.total,
                  sources: sources.join(' & '),
                })
              : result.total === 0
                ? t('dashboard.jobs.fetchNoJobs', 'No jobs ingested in this fetch — try different keywords.')
                : t('dashboard.jobs.liveFetchComplete', 'Live fetch complete.');
      const toastDetail = [detail, naukriHint].filter(Boolean).join(' ');
      if (toastDetail) {
        setLiveFetchToast({
          found: result.new_jobs_count ?? result.batch_jobs_count ?? result.total,
          sources,
          detail: toastDetail,
        });
        setTimeout(() => setLiveFetchToast(null), 10000);
      }

      setRecRefreshKey((k) => k + 1);
    } catch {
      setRecRefreshKey((k) => k + 1);
    } finally {
      window.clearTimeout(overlayCap);
      externalFetchingRef.current = false;
      setLiveFetching(false);
    }
  }, [profileJobTitle, profileLocation, experienceYears, profileSkillList, t]);

  // Keep overlay until ingest API + DB reload both finish.
  useEffect(() => {
    if (!liveFetchOverlay) return;
    if (!liveFetching && !recJobsLoading) {
      setLiveFetchOverlay(false);
    }
  }, [liveFetchOverlay, liveFetching, recJobsLoading]);

  // Server-side query uses only freshness + remote; other filters stay client-side.
  const serverFilterSlice = useMemo<JobFiltersState>(
    () => ({ ...defaultFilters, freshness: filters.freshness, remote: filters.remote }),
    [filters.freshness, filters.remote],
  );

  const serverFilterKey = useMemo(
    () => `${fetchKey}\0${serverFilterSlice.freshness}\0${serverFilterSlice.remote}`,
    [fetchKey, serverFilterSlice],
  );

  useLayoutEffect(() => {
    if (serverFilterKeyRef.current === null) {
      serverFilterKeyRef.current = serverFilterKey;
      return;
    }
    if (serverFilterKeyRef.current !== serverFilterKey) {
      serverFilterKeyRef.current = serverFilterKey;
      setPage(1);
    }
  }, [serverFilterKey]);

  const prevPageSize = useRef(pageSize);
  useLayoutEffect(() => {
    if (prevPageSize.current !== pageSize) {
      prevPageSize.current = pageSize;
      setPage(1);
    }
  }, [pageSize]);

  const needsCatalogFetch = isAdminMode;

  // ── AI-matched jobs (by recommendation id — same pool as Agent internal DB) ─
  useEffect(() => {
    if (isAdminMode || !authReady) {
      return;
    }
    if (!isSignedIn) {
      setRecJobs([]);
      setRecJobsLoading(false);
      return;
    }

    const gen = ++recFetchGenRef.current;
    let mounted = true;
    setRecJobsLoading(true);

    const run = async () => {
      try {
        const pool = await buildUnifiedRecommendedFeedCore({
          limit: RECOMMENDED_POOL_LIMIT,
          dbRecs,
          profile: userProfile,
          profileJobTitle,
          userCtx,
          hasSkillContext,
          skillContextReady,
        });

        if (mounted && gen === recFetchGenRef.current) setRecJobs(pool);
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err);
        console.warn('[Jobs] rec job fetch failed:', message, err);
        if (mounted && gen === recFetchGenRef.current) setRecJobs([]);
      } finally {
        if (mounted && gen === recFetchGenRef.current) setRecJobsLoading(false);
      }
    };

    void run();
    return () => { mounted = false; };
  }, [
    isAdminMode,
    authReady,
    isSignedIn,
    dbRecs,
    profileJobTitle,
    hasSkillContext,
    userCtx,
    skillContextReady,
    recRefreshKey,
    userProfile,
  ]);

  // ── Catalog fetch (admin board + table view pagination) ─────────────
  useEffect(() => {
    if (!needsCatalogFetch) {
      setLoading(false);
      return;
    }

    const gen = ++fetchGenRef.current;
    let mounted = true;
    setLoading(true);
    setError(null);

    const from = (page - 1) * pageSize;
    const to = from + pageSize - 1;

    const run = async () => {
      try {
        const dataQuery = applyServerPeerlistFilters(
          supabase.from('peerlist_jobs').select(PEERLIST_JOBS_SELECT),
          serverFilterSlice,
        )
          .order('last_seen_at', { ascending: false })
          .range(from, to);

        const countQuery = applyServerPeerlistFilters(
          supabase.from('peerlist_jobs').select('*', { count: 'exact', head: true }),
          serverFilterSlice,
        );

        const [sessionRes, dataRes, countRes] = await Promise.all([
          supabase.auth.getSession(),
          dataQuery,
          countQuery,
        ]);
        const { data: { session } } = sessionRes;
        const { data, error: dataErr } = dataRes;

        if (!mounted || gen !== fetchGenRef.current) return;

        setIsSignedIn(!!session);
        setIsOwner(
          (session?.user?.email ?? '').toLowerCase() === OWNER_EMAIL.toLowerCase(),
        );
        setAuthReady(true);

        if (dataErr) {
          setError(t('dashboard.jobs.loadFailed', 'Could not load jobs. Please try again.'));
          return;
        }

        const total = countRes.count ?? 0;
        if (countRes.error) {
          setServerTotal(null);
        } else {
          setServerTotal(total);
          const tp = total <= 0 ? 1 : Math.max(1, Math.ceil(total / pageSize));
          if (page > tp) {
            setJobs([]);
            setPage(tp);
            return;
          }
        }

        setJobs(mapAndFilterRows(data));
      } catch {
        if (mounted && gen === fetchGenRef.current) {
          setError(t('dashboard.jobs.loadError', 'Something went wrong loading jobs.'));
        }
      } finally {
        if (mounted && gen === fetchGenRef.current) {
          setLoading(false);
        }
      }
    };

    void run();
    return () => { mounted = false; };
  }, [page, pageSize, serverFilterSlice, needsCatalogFetch, i18n.language]);

  // Re-rank when skill context becomes available (profile OR resume)
  useEffect(() => {
    if (!hasSkillContext || !userCtx) return;
    setJobs((prev) => (prev.length === 0 ? prev : rankJobs(prev, userCtx)));
  }, [hasSkillContext, userCtx, jobs.length]);

  /**
   * Load Pinecone-based recommendations from `user_job_recommendations`.
   * If the table is empty or stale (> 6 h), fires `match-jobs-for-user`
   * in the background to refresh the vector-matched + LLM-reranked recs,
   * then reloads from the table once done.
   */
  useEffect(() => {
    if (!isSignedIn) {
      setDbRecs([]);
      setDbRecLoading(false);
      return;
    }
    let mounted = true;
    setDbRecLoading(true);

    const SIX_HOURS_MS = 6 * 60 * 60 * 1_000;

    const run = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session || !mounted) return;

        // 1. Read what's already in the table
        const existing = await getTopJobsForUser(session.user.id, RECOMMENDED_POOL_LIMIT);
        if (mounted) setDbRecs(existing);

        // 2. Check freshness: trigger match-jobs-for-user if absent or > 6 h old
        const newestTs = existing.length > 0
          ? Math.max(...existing.map(r => new Date(r.generated_at).getTime()))
          : 0;
        const isStale = Date.now() - newestTs > SIX_HOURS_MS;

        if ((existing.length === 0 || isStale) && mounted) {
          // Fire-and-forget: let the edge function run, then reload results.
          // `match-jobs-for-user` respects its own 6h cache server-side too.
          supabase.functions
            .invoke('match-jobs-for-user', {
              headers: { Authorization: `Bearer ${session.access_token}` },
            })
            .then(async ({ error }) => {
              if (error || !mounted) return;
              // Reload fresh recs after the pipeline completes
              const fresh = await getTopJobsForUser(session.user.id, RECOMMENDED_POOL_LIMIT);
              if (mounted) setDbRecs(fresh);
            })
            .catch(() => { /* best-effort — fail silently */ });
        }
      } catch {
        // Enhancement-only — fail silently
      } finally {
        if (mounted) setDbRecLoading(false);
      }
    };

    void run();
    return () => { mounted = false; };
  }, [isSignedIn, recRefreshKey]);

  // Deep-link: ?open=<external_slug>
  const deepLinkHandled = useRef(false);
  useEffect(() => {
    if (deepLinkHandled.current) return;

    const openJobById = async (jobId: string): Promise<boolean> => {
      const inList = jobs.find((j) => j.id === jobId);
      if (inList) {
        setSelectedJob(inList);
        return true;
      }
      const { data, error: fetchErr } = await supabase
        .from('peerlist_jobs')
        .select('*')
        .eq('id', jobId)
        .eq('is_active', true)
        .maybeSingle();
      if (fetchErr || !data) return false;
      const mapped = toJob(data as PeerlistRow);
      setSelectedJob(mapped);
      return true;
    };

    const run = async () => {
      const storedJobId = sessionStorage.getItem('ggj_jobs_open_job_id');
      if (storedJobId) {
        sessionStorage.removeItem('ggj_jobs_open_job_id');
        if (await openJobById(storedJobId)) {
          deepLinkHandled.current = true;
          return;
        }
      }

      const params = new URLSearchParams(window.location.search);
      const jobIdParam = params.get('jobId');
      if (jobIdParam && (await openJobById(jobIdParam))) {
        deepLinkHandled.current = true;
        return;
      }

      if (!jobs.length) return;
      const slug = params.get('open');
      if (!slug) return;
      const job = jobs.find((j) => j.external_job_id === slug);
      if (job) {
        setSelectedJob(job);
        deepLinkHandled.current = true;
      }
    };

    void run();
  }, [jobs]);

  // Post-optimizer apply confirmation bridge (sessionStorage handoff).
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem('jobs.pendingConfirmation');
      if (!raw) return;
      sessionStorage.removeItem('jobs.pendingConfirmation');
      const parsed = JSON.parse(raw) as { job: Job };
      if (parsed.job) {
        setConfirmationJob(parsed.job);
        setConfirmedApplicationId(null);
      }
    } catch {
      // ignore malformed entries
    }
  }, []);

  // Saved jobs hook — handles optimistic toggle + error rollback.
  const { savedKeys, toggle: toggleSave, errorMessage, clearError } =
    useSavedJobs(isSignedIn);

  const filteredJobs = useMemo(() => applyJobFilters(jobs, filters), [jobs, filters]);

  const totalPages = useMemo(() => {
    if (serverTotal === null) return 1;
    if (serverTotal <= 0) return 1;
    return Math.max(1, Math.ceil(serverTotal / pageSize));
  }, [serverTotal, pageSize]);

  // ── Handlers (unchanged) ───────────────────────────────────────────

  const handleToggleSave = useCallback(
    (job: Job) => { void toggleSave(job); },
    [toggleSave],
  );

  /** Route optimize flow to Agent (canvas + apply tracking). */
  const handleApply = useCallback((job: Job) => {
    writeAgentOptimizeHandoff(job);
    setSelectedJob(null);
    if (onNavigate) {
      onNavigate('agent');
      return;
    }
    window.dispatchEvent(
      new CustomEvent('jobs:navigate', { detail: { page: 'agent' } }),
    );
  }, [onNavigate]);

  const handleView = useCallback((_job: Job) => {}, []);

  const handleTakedown = useCallback((slug: string) => {
    setJobs((prev) => prev.filter((j) => j.external_job_id !== slug));
  }, []);

  const handleCardClick = useCallback((job: Job) => setSelectedJob(job), []);
  const closeDrawer = useCallback(() => setSelectedJob(null), []);

  const handleExplainMatch = useCallback((job: Job) => setExplainJob(job), []);

  const explainBreakdown = useMemo(() => {
    if (!explainJob) {
      return buildJobExplainerBreakdown({ matchScore: 0 });
    }
    return buildJobExplainerBreakdown(
      explainJob,
      recByJobId.get(explainJob.id),
      {
        experienceYears: userProfile?.experienceYears,
        industry: userProfile?.industry,
        currentRole: userProfile?.currentRole ?? userProfile?.headline,
      },
    );
  }, [explainJob, recByJobId, userProfile]);

  const handleImproveMatch = useCallback(() => {
    if (!explainJob) return;
    writeAgentOptimizeHandoff(explainJob);
    setExplainJob(null);
    handleApply(explainJob);
  }, [explainJob, handleApply]);

  // ── Layout toggle ─────────────────────────────────────────────────
  // viewMode state declared above with other page state

  // ── Table sort state ──────────────────────────────────────────────
  type TableSortCol = 'company' | 'title' | 'match' | 'source' | 'role' | 'location' | 'posted';
  const [tableSortCol, setTableSortCol]     = useState<TableSortCol>('posted');
  const [tableSortDir, setTableSortDir]     = useState<'asc' | 'desc'>('desc');
  const [tableSelected, setTableSelected]   = useState<Set<string>>(new Set());

  const handleTableSort = useCallback((col: TableSortCol) => {
    setTableSortCol(prev => {
      if (prev === col) { setTableSortDir(d => d === 'asc' ? 'desc' : 'asc'); return prev; }
      setTableSortDir('desc');
      return col;
    });
  }, []);

  const toggleTableSelect = useCallback((id: string) => {
    setTableSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }, []);

  // ── Recommendation quick-filter state ──────────────────────────────
  const [recToolbar, setRecToolbar] = useState<RecToolbarFilters>(defaultRecToolbarFilters);

  useLayoutEffect(() => {
    setRecPage(1);
  }, [recToolbar, filters, dbRecs.length]);

  const recommendedBase = useMemo(() => {
    let pool: Job[];

    if (!isAdminMode && recJobs.length > 0) {
      pool = applyJobFilters(recJobs, filters);
    } else {
      pool = buildRecommendedFallbackPool(
        filteredJobs,
        dbRecs,
        hasSkillContext,
        profileJobTitle,
      );
    }

    if (!isAdminMode && userProfile) {
      const roleTargets = extractRoleTargets(userProfile, profileJobTitle);
      pool = filterBlatantLocationConflicts(pool, userProfile);
      pool = sortJobsByPreferenceRelevance(pool, userProfile, roleTargets);
    }

    return balanceChannelDisplayOrder(pool);
  }, [
    filteredJobs,
    dbRecs,
    hasSkillContext,
    profileJobTitle,
    isAdminMode,
    recJobs,
    filters,
    userProfile,
  ]);

  function buildRecommendedFallbackPool(
    catalogJobs: Job[],
    dbRecsList: RecommendationResult[],
    hasSkills: boolean,
    jobTitle: string | undefined,
  ): Job[] {
    const pineconeScoreMap = new Map<string, number>(
      dbRecsList.map((r) => [r.job_id, r.score]),
    );

    let pool: Job[] = catalogJobs.map((j) => {
      const pineconeScore = pineconeScoreMap.get(j.id);
      if (pineconeScore != null) {
        const domainScore = roleAlignmentScore(jobTitle, undefined, j.title);
        const isCrossDomainMismatch = domainScore !== 50 && domainScore < 30;
        if (isCrossDomainMismatch) {
          return { ...j, matchScore: Math.min(20, Math.round(pineconeScore * 0.35)) };
        }
        return { ...j, matchScore: Math.round(pineconeScore) };
      }
      return j;
    });

    pool.sort((a, b) => {
      const aIsPinecone = pineconeScoreMap.has(a.id);
      const bIsPinecone = pineconeScoreMap.has(b.id);
      if (aIsPinecone && !bIsPinecone) return -1;
      if (!aIsPinecone && bIsPinecone) return 1;
      return (b.matchScore ?? 0) - (a.matchScore ?? 0);
    });

    return pool;
  }

  const recommendedFilteredPool = useMemo(
    () => applyRecToolbarFilters(recommendedBase, recToolbar),
    [recommendedBase, recToolbar],
  );

  const recommendedFilteredCount = recommendedFilteredPool.length;

  const recTotalPages = useMemo(
    () => Math.max(1, Math.ceil(recommendedFilteredCount / REC_GRID_PAGE_SIZE)),
    [recommendedFilteredCount],
  );

  // Clamp the active page whenever the filtered pool shrinks — e.g. after FETCH
  // Live rebuilds the pool or a match-fit filter narrows results. Without this,
  // `recPage` can point past the end of the pool, so the grid slices to an empty
  // array *and* the pagination control hides (recTotalPages === 1), trapping the
  // user on a blank page even though matching jobs still exist.
  useEffect(() => {
    setRecPage((p) => Math.min(Math.max(1, p), recTotalPages));
  }, [recTotalPages]);

  const recommendedJobs = useMemo(() => {
    const safePage = Math.min(Math.max(1, recPage), recTotalPages);
    const start = (safePage - 1) * REC_GRID_PAGE_SIZE;
    return recommendedFilteredPool.slice(start, start + REC_GRID_PAGE_SIZE);
  }, [recommendedFilteredPool, recPage, recTotalPages]);

  // Table view: non-admin uses the same unified feed as grid (client-side); admin uses server catalog.
  const tableSourceJobs = useMemo(
    () => (isAdminMode ? filteredJobs : recommendedFilteredPool),
    [isAdminMode, filteredJobs, recommendedFilteredPool],
  );

  const sortedTableJobs = useMemo(() => {
    const arr = [...tableSourceJobs];
    arr.sort((a, b) => {
      let va: string | number = 0;
      let vb: string | number = 0;
      switch (tableSortCol) {
        case 'company':  va = (a.company ?? '').toLowerCase();       vb = (b.company ?? '').toLowerCase(); break;
        case 'title':    va = (a.title ?? '').toLowerCase();         vb = (b.title ?? '').toLowerCase(); break;
        case 'match':    va = a.matchScore ?? 0;                     vb = b.matchScore ?? 0; break;
        case 'source':   va = (a.source_channel ?? a.source ?? ''); vb = (b.source_channel ?? b.source ?? ''); break;
        case 'role':     va = (a.job_type ?? '').toLowerCase();      vb = (b.job_type ?? '').toLowerCase(); break;
        case 'location': va = (a.location ?? '').toLowerCase();      vb = (b.location ?? '').toLowerCase(); break;
        case 'posted':   va = a.posted_at ?? a.created ?? '';        vb = b.posted_at ?? b.created ?? ''; break;
      }
      if (va < vb) return tableSortDir === 'asc' ? -1 : 1;
      if (va > vb) return tableSortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return arr;
  }, [tableSourceJobs, tableSortCol, tableSortDir]);

  const tableTotalPages = useMemo(() => {
    if (isAdminMode) return totalPages;
    return Math.max(1, Math.ceil(sortedTableJobs.length / pageSize));
  }, [isAdminMode, totalPages, sortedTableJobs.length, pageSize]);

  const displayedTableJobs = useMemo(() => {
    if (isAdminMode) return sortedTableJobs;
    const start = (page - 1) * pageSize;
    return sortedTableJobs.slice(start, start + pageSize);
  }, [isAdminMode, sortedTableJobs, page, pageSize]);

  const toggleSelectAll = useCallback(() => {
    setTableSelected(prev =>
      prev.size === displayedTableJobs.length
        ? new Set()
        : new Set(displayedTableJobs.map(j => j.id)),
    );
  }, [displayedTableJobs]);

  useLayoutEffect(() => {
    setPage(1);
    setTableSelected(new Set());
  }, [viewMode]);

  useEffect(() => {
    setPage((p) => Math.min(Math.max(1, p), tableTotalPages));
  }, [tableTotalPages]);

  const hasGridContent = recJobs.length > 0;
  const awaitingSkillRank = isSignedIn && skillContextReady === false;
  const pageLoading = isAdminMode
    ? loading
    : viewMode === 'table'
      ? !authReady || (recJobsLoading && recJobs.length === 0)
      : !authReady
        || awaitingSkillRank
        || (!hasGridContent && (dbRecLoading || recJobsLoading));

  // Grid mode still needs auth/session flags without running the catalog query.
  useEffect(() => {
    if (needsCatalogFetch) return;
    let mounted = true;
    void supabase.auth.getSession().then(({ data: { session } }) => {
      if (!mounted) return;
      setIsSignedIn(!!session);
      setIsOwner(
        (session?.user?.email ?? '').toLowerCase() === OWNER_EMAIL.toLowerCase(),
      );
      setAuthReady(true);
    });
    return () => { mounted = false; };
  }, [needsCatalogFetch, fetchKey]);

  // ---- Render ---------------------------------------------------------------

  void hasResume; // used indirectly via userCtx

  if (pageLoading) {
    return (
      <div className="flex flex-1 items-center justify-center w-full" style={{ minHeight: 'calc(100vh - 48px)' }}>
        <LoadingSpinner size="lg" showText={false} />
      </div>
    );
  }

  return (
    <div
      key={i18n.language}
      data-tour="page-jobs"
      className="ds-page ds-jobs-page relative flex flex-col flex-1 min-h-0 gap-3 p-4 pt-3 sm:p-5 sm:pt-4 pb-[max(0.5rem,env(safe-area-inset-bottom))]"
      style={{ minHeight: 'calc(100vh - 48px)' }}
    >
      <header className="ds-jobs-page-header flex-shrink-0">
        <h1 className="t-page-title leading-tight">
          {isAdminMode
            ? t('dashboard.jobs.adminTitle', 'Job Board')
            : t('dashboard.jobs.title', 'Jobs for you')}
        </h1>
        <p className="t-meta mt-1 max-w-2xl leading-snug" style={{ color: 'var(--ds-text-muted)' }}>
          {isAdminMode
            ? t('dashboard.jobs.adminSubtitle', 'All jobs pulled from various channels')
            : t(
                  'dashboard.jobs.subtitle',
                  'All channels in your recommended pool — Peerlist, LinkedIn, and Naukri. Use FETCH Live to refresh with jobs matched to your role.',
                )}
        </p>
        {!isAdminMode ? (
          <p className="t-meta mt-2 max-w-2xl leading-snug" style={{ color: 'var(--ds-text-muted)' }}>
            {t(
              'dashboard.jobs.trustNote',
              'You decide what to apply to and what to share. GoGrabJob prepares tailored resumes and application packs — you review and submit.',
            )}
          </p>
        ) : null}
      </header>

      <div data-tour="jobs-filters" className="flex flex-shrink-0 flex-wrap items-center gap-1">
        <button
          type="button"
          onClick={() => setActiveTab('all')}
          aria-selected={activeTab === 'all'}
          className="ds-chip ds-chip-button flex items-center gap-1.5 min-h-[44px] px-3 touch-manipulation"
        >
          <TrendingUp className="w-4 h-4" />
          {t('dashboard.jobs.tabAll', 'All')}
        </button>
        <button
          type="button"
          onClick={() => setActiveTab('saved')}
          aria-selected={activeTab === 'saved'}
          className="ds-chip ds-chip-button flex items-center gap-1.5 min-h-[44px] px-3 touch-manipulation"
          disabled={!isSignedIn}
          title={isSignedIn ? undefined : t('dashboard.jobs.signInToSaved', 'Sign in to view saved jobs')}
        >
          <Bookmark className="w-4 h-4" />
          {t('dashboard.jobs.tabSaved', 'Saved')}
          {isSignedIn && savedKeys.size > 0 && (
            <span className="ds-chip ds-chip-brand t-num ml-0.5 px-1.5 py-0.5">
              {savedKeys.size}
            </span>
          )}
        </button>
      </div>

      {/* Transient error toast */}
      {errorMessage && (
        <div className="ds-recessed-danger flex items-center justify-between gap-3 px-3 py-2.5">
          <div className="flex items-center gap-2 min-w-0">
            <AlertCircle className="w-4 h-4 shrink-0" style={{ color: 'var(--ds-danger)' }} />
            <p className="t-meta">{errorMessage}</p>
          </div>
          <button
            type="button"
            onClick={clearError}
            aria-label={t('dashboard.jobs.dismiss', 'Dismiss')}
            className="ds-btn ds-btn-ghost ds-btn-sm shrink-0 touch-manipulation"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Signed-out hint */}
      {!isSignedIn && !loading && (
        <div className="ds-recessed flex items-center justify-between gap-3"
          style={{ background: 'var(--ds-brand-soft)', borderColor: 'var(--ds-brand-border)' }}>
          <p className="t-meta">
            {t('dashboard.jobs.signedOutHint', 'Sign in to save jobs and use')} <strong>{t('dashboard.jobs.optimizeAndApply', 'Optimize & Apply')}</strong>.
          </p>
          <a
            href="#/login"
            className="ds-btn ds-btn-primary shrink-0 touch-manipulation"
          >
            {t('dashboard.jobs.signIn', 'Sign in')}
          </a>
        </div>
      )}

      {/* ── Saved tab — same compact list as before ─────────────────── */}
      {activeTab === 'saved' && isSignedIn && (
        <div className="flex-1 min-h-0">
          <SavedJobsList
            savedKeys={savedKeys}
            onToggleSave={handleToggleSave}
            onApply={handleApply}
            onView={handleView}
            onCardClick={handleCardClick}
          />
        </div>
      )}

      {/* ── All tab ─────────────────────────────────────────────────── */}
      {activeTab === 'all' && (
        <>
          {/* Unified filter + sort + view toggle + refresh bar */}
          {/* External refresh toast */}
          {liveFetchToast && (
            <div className="ds-recessed-ok flex items-center gap-2">
              <Sparkles className="w-3.5 h-3.5 shrink-0" />
              <span className="t-meta" style={{ color: 'var(--ds-ok-text)' }}>
                {liveFetchToast.detail
                  ?? (liveFetchToast.found > 0
                    ? t('dashboard.jobs.liveFetchStored', {
                        defaultValue: 'Stored {{count}} jobs from {{sources}}',
                        count: liveFetchToast.found,
                        sources: liveFetchToast.sources.join(' & '),
                      })
                    : t('dashboard.jobs.liveFetchComplete', 'Live fetch complete.'))}
              </span>
              <button
                type="button"
                onClick={() => setLiveFetchToast(null)}
                className="ds-btn ds-btn-ghost ds-btn-sm ml-auto shrink-0"
                aria-label={t('dashboard.jobs.dismiss', 'Dismiss')}
              ><X className="w-3 h-3" /></button>
            </div>
          )}

          <JobFilters
            jobs={!isAdminMode ? recJobs : jobs}
            value={filters}
            onChange={setFilters}
            totalCount={!isAdminMode ? recommendedBase.length : jobs.length}
            filteredCount={!isAdminMode ? recommendedFilteredCount : filteredJobs.length}
            onFetchLive={!isAdminMode && isSignedIn ? handleFetchLive : undefined}
            isFetchingLive={liveFetchOverlay}
            viewMode={viewMode}
            onViewModeChange={setViewMode}
            experienceYears={experienceYears}
            showRecFilters={!isAdminMode}
            recToolbar={recToolbar}
            onRecToolbarChange={setRecToolbar}
            recFilterLoading={dbRecLoading}
          />


          {/* Fetch error */}
          {error && (
            <div className="ds-recessed-danger flex items-start justify-between gap-3">
              <div className="flex items-start gap-2 min-w-0">
                <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" style={{ color: 'var(--ds-danger)' }} />
                <p className="t-meta">{error}</p>
              </div>
              <button
                type="button"
                onClick={retry}
                className="ds-btn ds-btn-secondary ds-btn-sm shrink-0 touch-manipulation"
              >
                <RefreshCw className="w-3.5 h-3.5" />
                {t('dashboard.jobs.retry', 'Retry')}
              </button>
            </div>
          )}

          {/* ── Grid mode: Recommended section with filter chips + card grid ── */}
          {!pageLoading && !error && !isAdminMode && viewMode === 'grid' && (
            <RecommendedSection
              jobs={recommendedJobs}
              totalCount={recommendedBase.length}
              lookupApplied={lookupApplied}
              savedKeys={savedKeys}
              onCardClick={handleCardClick}
              onExplainMatch={handleExplainMatch}
              onToggleSave={handleToggleSave}
              onApply={handleApply}
              onView={handleView}
              isOwner={isOwner}
              onTakedown={handleTakedown}
              isSignedIn={isSignedIn}
              hasSkillContext={hasSkillContext}
              profileSkills={profileSkillList}
              isRanking={dbRecLoading && !liveFetchOverlay}
              recToolbar={recToolbar}
              onRecToolbarChange={setRecToolbar}
              onClearFilters={() => { setFilters(defaultFilters); setRecToolbar(defaultRecToolbarFilters); }}
              geoPrefsActive={hasGeoPreferences(userProfile)}
              preferredLocation={userProfile?.location ?? null}
              currentTitle={(userProfile?.currentRole || profileJobTitle || '').trim()}
              hiddenOpportunityPool={recommendedBase}
            />
          )}

          {!pageLoading && !error && !isAdminMode && viewMode === 'grid' && recTotalPages > 1 && (
            <div className="mt-2 flex items-center justify-center gap-1 border-t pt-3" style={{ borderColor: 'var(--ds-border)' }}>
              <button
                type="button"
                disabled={recPage <= 1}
                onClick={() => setRecPage((p) => Math.max(1, p - 1))}
                aria-label={t('dashboard.jobs.prevPage', 'Previous page')}
                className="ds-btn ds-btn-ghost min-h-[44px] min-w-[44px] touch-manipulation disabled:opacity-30"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              <span className="t-body t-num px-2 min-w-[7rem] text-center">
                {t('dashboard.jobs.pageNOfM', {
                  defaultValue: 'Page {{page}} of {{totalPages}}',
                  page: recPage,
                  totalPages: recTotalPages,
                })}
              </span>
              <button
                type="button"
                disabled={recPage >= recTotalPages}
                onClick={() => setRecPage((p) => Math.min(recTotalPages, p + 1))}
                aria-label={t('dashboard.jobs.nextPage', 'Next page')}
                className="ds-btn ds-btn-ghost min-h-[44px] min-w-[44px] touch-manipulation disabled:opacity-30"
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            </div>
          )}

          {/* ── Admin grid mode: plain grid (no Recommended header) ── */}
          {!pageLoading && !error && isAdminMode && viewMode === 'grid' && (
            <section data-tour="jobs-list" className="flex flex-col gap-3" aria-label={t('dashboard.jobs.allJobs', 'All Jobs')}>
              {filteredJobs.length === 0 ? (
                <div className="ds-card text-center">
                  <Briefcase className="w-12 h-12 mx-auto mb-3 opacity-40" style={{ color: 'var(--ds-text-muted)' }} />
                  <p className="t-card-title mb-1">{t('dashboard.jobs.noJobsMatch', 'No jobs match your filter')}</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
                  {filteredJobs.map((job) => (
                    <JobCard
                      key={job.id}
                      job={job}
                      isSaved={savedKeys.has(`${job.source}:${job.external_job_id ?? job.id}`)}
                      onToggleSave={handleToggleSave}
                      onApply={handleApply}
                      onView={handleView}
                      onCardClick={handleCardClick}
                      onExplainMatch={handleExplainMatch}
                      isOwner={isOwner}
                      onTakedown={handleTakedown}
                    />
                  ))}
                </div>
              )}
            </section>
          )}

          {/* ── Table mode: single unified table (all roles, no Recommended section) ── */}
          {!pageLoading && !error && viewMode === 'table' && (
            <section data-tour="jobs-list" className="flex flex-col gap-3" aria-label={t('dashboard.jobs.allOpenRoles', 'All open roles')}>

              {tableSourceJobs.length === 0 ? (
                <div className="ds-card text-center">
                  <Briefcase className="w-12 h-12 mx-auto mb-3 opacity-40" style={{ color: 'var(--ds-text-muted)' }} />
                  <p className="t-card-title mb-1">
                    {isAdminMode && serverTotal === 0
                      ? t('dashboard.jobs.noJobsYet', 'No jobs yet')
                      : isAdminMode && jobs.length === 0
                        ? t('dashboard.jobs.noJobsInBatch', 'No jobs in this page batch')
                        : t('dashboard.jobs.noJobsMatch', 'No jobs match your filter')}
                  </p>
                  <p className="t-meta">
                    {isAdminMode && serverTotal === 0
                      ? t('dashboard.jobs.checkBackSoon', 'No jobs yet — check back soon, we ingest every 6 h.')
                      : isAdminMode && jobs.length === 0
                        ? t('dashboard.jobs.tryAnotherPage', 'Nothing in this page batch matches — try another page or wait for ingest.')
                        : t('dashboard.jobs.tryDifferentFilter', 'Try a different keyword or clear the filters.')}
                  </p>
                </div>
              ) : (
                /* ── Table view — Untitled UI style ── */
                (() => {
                  const SortHead: React.FC<{ col: TableSortCol; label: string; width?: number }> = ({ col, label, width }) => {
                    const active = tableSortCol === col;
                    const arrow  = active ? (tableSortDir === 'asc' ? ' ↑' : ' ↓') : ' ↕';
                    return (
                      <th
                        onClick={() => handleTableSort(col)}
                        className="px-4 py-3 text-left whitespace-nowrap cursor-pointer select-none"
                        style={{ minWidth: width, background: 'var(--ds-bg-recessed)' }}
                      >
                        <span className={`t-label flex items-center gap-1${active ? ' t-brand' : ''}`}>
                          {label}
                          <span className="opacity-60">{arrow}</span>
                        </span>
                      </th>
                    );
                  };
                  const allChecked = tableSelected.size > 0 && tableSelected.size === displayedTableJobs.length;
                  const someChecked = tableSelected.size > 0 && !allChecked;
                  return (
                    <div className="ds-card-flush">
                      <div className="overflow-x-auto">
                        <table className="ds-table" style={{ minWidth: 1100 }}>
                          <thead>
                            <tr>
                              {/* Select-all */}
                              <th className="w-10 px-4 py-3" style={{ background: 'var(--ds-bg-recessed)' }}>
                                <input type="checkbox" className="w-3.5 h-3.5 rounded accent-[var(--ds-brand)] cursor-pointer"
                                  checked={allChecked} ref={(el) => { if (el) el.indeterminate = someChecked; }}
                                  onChange={toggleSelectAll} />
                              </th>
                              <SortHead col="company"  label={t('dashboard.jobs.tableColCompany', 'Company')}   width={160} />
                              <SortHead col="title"    label={t('dashboard.jobs.tableColJob', 'Job')}        width={200} />
                              <SortHead col="match"    label={t('dashboard.jobs.tableColMatch', 'Match %')}    width={90}  />
                              <SortHead col="source"   label={t('dashboard.jobs.tableColSource', 'Source')}     width={90}  />
                              <SortHead col="role"     label={t('dashboard.jobs.tableColRole', 'Role')}       width={110} />
                              <SortHead col="location" label={t('dashboard.jobs.tableColLocation', 'Location')}   width={130} />
                              <th className="px-4 py-3 text-left whitespace-nowrap" style={{ minWidth: 160, background: 'var(--ds-bg-recessed)' }}>
                                <span className="t-label">{t('dashboard.jobs.tableColSkills', 'Skills')}</span>
                              </th>
                              <SortHead col="posted"   label={t('dashboard.jobs.tableColPosted', 'Posted')}     width={90}  />
                              <th className="px-4 py-3" style={{ minWidth: 120, background: 'var(--ds-bg-recessed)' }} />
                            </tr>
                          </thead>
                          <tbody>
                            {displayedTableJobs.map((job, idx) => (
                              <JobTableRow
                                key={job.id}
                                job={job}
                                isSaved={savedKeys.has(`${job.source}:${job.external_job_id ?? job.id}`)}
                                isSelected={tableSelected.has(job.id)}
                                isEven={idx % 2 === 1}
                                onSelect={() => toggleTableSelect(job.id)}
                                onToggleSave={handleToggleSave}
                                onApply={handleApply}
                                onView={handleView}
                                onRowClick={handleCardClick}
                                onExplainMatch={handleExplainMatch}
                              />
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  );
                })()
              )}

            </section>
          )}

          {/* ── Shared pagination — shown for both grid and table modes ── */}
          {!pageLoading && !error && tableTotalPages > 1 && (isAdminMode || viewMode === 'table') && (
            <div className="mt-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border-t pt-3" style={{ borderColor: 'var(--ds-border)' }}>
              <label className="flex items-center gap-2 t-meta w-full sm:w-auto">
                <span className="shrink-0">{t('dashboard.jobs.rowsPerPage', 'Rows per page')}</span>
                <select
                  value={pageSize}
                  onChange={(e) => setPageSize(Number(e.target.value))}
                  aria-label={t('dashboard.jobs.rowsPerPage', 'Rows per page')}
                  className="ds-select min-h-[44px] w-full sm:w-auto min-w-[5rem]"
                >
                  {PAGE_SIZE_OPTIONS.map((n) => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
              </label>
              <div className="flex items-center justify-center gap-1 w-full sm:w-auto">
                <button
                  type="button"
                  disabled={page <= 1 || (isAdminMode && loading)}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  aria-label={t('dashboard.jobs.prevPage', 'Previous page')}
                  className="ds-btn ds-btn-ghost min-h-[44px] min-w-[44px] touch-manipulation"
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
                <span className="t-body t-num px-2 min-w-[7rem] text-center">
                  {t('dashboard.jobs.pageNOfM', { defaultValue: 'Page {{page}} of {{totalPages}}', page, totalPages: tableTotalPages })}
                </span>
                <button
                  type="button"
                  disabled={page >= tableTotalPages || (isAdminMode && loading)}
                  onClick={() => setPage((p) => Math.min(tableTotalPages, p + 1))}
                  aria-label={t('dashboard.jobs.nextPage', 'Next page')}
                  className="ds-btn ds-btn-ghost min-h-[44px] min-w-[44px] touch-manipulation"
                >
                  <ChevronRight className="h-5 w-5" />
                </button>
              </div>
            </div>
          )}

        </>
      )}

      {liveFetchOverlay && (
        <div
          className="fixed inset-0 z-[5000] flex flex-col items-center justify-center gap-3 px-6"
          style={{
            background: 'color-mix(in srgb, var(--ds-bg-page) 78%, transparent)',
            backdropFilter: 'blur(3px)',
          }}
          role="status"
          aria-live="polite"
          aria-busy="true"
        >
          <LoadingSpinner
            size="xl"
            text={t(
              'dashboard.jobs.fetchingLive',
              'Fetching live jobs from external channels…',
            )}
          />
        </div>
      )}

      <JobMatchExplainerModal
        open={!!explainJob}
        jobTitle={explainJob?.title ?? ''}
        company={explainJob?.company ?? ''}
        breakdown={explainBreakdown}
        onClose={() => setExplainJob(null)}
        onOptimize={explainJob ? handleImproveMatch : undefined}
      />

      {/* Job Detail side drawer (WO-11) — unchanged. */}
      <JobDetailDrawer
        job={selectedJob}
        onClose={closeDrawer}
        onApply={handleApply}
        onView={handleView}
        onToggleSave={handleToggleSave}
        statusBadge={
          selectedJob
            ? (() => {
                const info = lookupApplied(selectedJob.id, selectedJob.title, selectedJob.company);
                return info ? <JobWorkflowStatusBadge info={info} size="md" /> : null;
              })()
            : null
        }
        isSaved={
          selectedJob
            ? savedKeys.has(`${selectedJob.source}:${selectedJob.external_job_id ?? selectedJob.id}`)
            : false
        }
        allJobs={jobs}
        onSelectSimilar={handleCardClick}
      />

      {/* WO-17 PR-D apply-confirmation modal — unchanged. */}
      {confirmationJob && (
        <ApplyConfirmationModal
          isOpen={!!confirmationJob}
          onClose={() => {
            setConfirmationJob(null);
            setConfirmedApplicationId(null);
          }}
          job={confirmationJob}
          applicationId={confirmedApplicationId}
        />
      )}
    </div>
  );
};

export default React.memo(Jobs);

// ═════════════════════════════════════════════════════════════════════
// Sub-cards used by the workspace overview (TASK 1-7)
// ═════════════════════════════════════════════════════════════════════

// ── Rec quick-filter (toolbar dropdown in JobFilters) ────────────────────────

interface RecommendedSectionProps {
  jobs:               Job[];
  totalCount:         number;
  lookupApplied:      (jobId: string, title: string, company: string) => AppliedJobInfo | null;
  savedKeys:          Set<string>;
  onCardClick:        (job: Job) => void;
  onExplainMatch?:    (job: Job) => void;
  onToggleSave:       (job: Job) => void;
  onApply:            (job: Job) => void;
  onView:             (job: Job) => void;
  isOwner:            boolean;
  onTakedown:         (slug: string) => void;
  isSignedIn:         boolean;
  hasSkillContext:    boolean;
  profileSkills:      string[];
  isRanking:          boolean;
  recToolbar:          RecToolbarFilters;
  onRecToolbarChange:  (f: RecToolbarFilters) => void;
  /** Clears ALL active filters (both rec chips and main filters) */
  onClearFilters?:    () => void;
  geoPrefsActive?:    boolean;
  preferredLocation?: string | null;
  currentTitle?:      string;
  hiddenOpportunityPool?: Job[];
}

const RecommendedSection: React.FC<RecommendedSectionProps> = ({
  jobs,
  totalCount,
  lookupApplied,
  savedKeys,
  onCardClick,
  onExplainMatch,
  onToggleSave,
  onApply,
  onView,
  isOwner,
  onTakedown,
  isSignedIn,
  hasSkillContext,
  profileSkills,
  isRanking,
  recToolbar,
  onRecToolbarChange,
  onClearFilters,
  geoPrefsActive = false,
  preferredLocation = null,
  currentTitle = '',
  hiddenOpportunityPool = [],
}) => {
  const { t, i18n } = useTranslation();
  const hiddenOps = buildHiddenOpportunities(
    currentTitle,
    jobListToHiddenSources(hiddenOpportunityPool),
  );

  return (
  <section key={i18n.language} className="flex flex-col gap-6">
    {geoPrefsActive && jobs.length > 0 && (
      <div className="ds-recessed flex items-start gap-2 px-3 py-2"
        style={{ background: 'var(--ds-brand-soft)', borderColor: 'var(--ds-brand-border)' }}>
        <Sparkles className="w-3.5 h-3.5 shrink-0 mt-0.5" style={{ color: 'var(--ds-brand)' }} />
        <p className="t-meta leading-snug">
          {t('dashboard.jobs.results.geoBanner', {
            defaultValue: 'Your recommended pool — all channels. Preferences rank results; use FETCH Live for fresh matches.{{location}}',
            location: preferredLocation ? ` (${preferredLocation})` : '',
          })}
        </p>
      </div>
    )}

    {/* No-skills nudge — only shown once, compact inline */}
    {isSignedIn && !hasSkillContext && (
      <div className="ds-recessed flex items-center gap-2"
        style={{ background: 'var(--ds-brand-soft)', borderColor: 'var(--ds-brand-border)' }}>
        <Zap className="w-3.5 h-3.5 shrink-0" style={{ color: 'var(--ds-brand)' }} />
        <p className="t-meta">
          {t('dashboard.jobs.results.addSkillsInline', 'Add skills in Settings → Profile to unlock match scores.')}
        </p>
      </div>
    )}

    {isSignedIn && currentTitle && (
      <HiddenOpportunities
        currentTitle={currentTitle}
        opportunities={hiddenOps}
        showPlaceholder
        onViewJob={(jobId) => {
          const job = hiddenOpportunityPool.find((j) => j.id === jobId)
            ?? jobs.find((j) => j.id === jobId);
          if (job) onCardClick(job);
        }}
      />
    )}

    {/* Card grid */}
    {jobs.length === 0 ? (
      <div className="ds-card text-center flex flex-col items-center gap-3 py-8 px-4">
        <Briefcase className="w-12 h-12 opacity-40" style={{ color: 'var(--ds-text-muted)' }} />
        {!hasActiveRecToolbarFilters(recToolbar) ? (
          geoPrefsActive ? (
            <>
              <p className="t-card-title">{t('dashboard.jobs.results.noRegionTitle', 'No jobs in your preferred region')}</p>
              <p className="t-meta max-w-md">
                {t('dashboard.jobs.results.noRegionHint', {
                  defaultValue: 'Nothing in your feed matches your saved location{{location}} and work-mode preferences. Try FETCH Live for fresh listings, or update Settings → Preferences.',
                  location: preferredLocation ? ` (${preferredLocation})` : '',
                })}
              </p>
            </>
          ) : (
            <>
              <p className="t-card-title">{t('dashboard.jobs.results.noFeedTitle', 'No jobs in your feed yet')}</p>
              <p className="t-meta max-w-md">
                {t('dashboard.jobs.results.noFeedHint', 'Add your preferred cities under Settings → Preferences, then use FETCH Live to pull matching listings.')}
              </p>
            </>
          )
        ) : (
          <>
            <p className="t-card-title">{t('dashboard.jobs.noJobsMatch', 'No jobs match your filter')}</p>
            <div className="flex gap-2 flex-wrap justify-center">
              {hasActiveRecToolbarFilters(recToolbar) && (
                <button
                  type="button"
                  onClick={() => onRecToolbarChange(defaultRecToolbarFilters)}
                  className="ds-btn ds-btn-primary ds-btn-sm"
                >
                  {t('dashboard.jobs.results.showAllMatches', 'Show all matches')}
                </button>
              )}
              {onClearFilters && (
                <button
                  type="button"
                  onClick={onClearFilters}
                  className="ds-btn ds-btn-ghost ds-btn-sm"
                >
                  {t('dashboard.jobs.filters.clearAll', 'Clear all')}
                </button>
              )}
            </div>
          </>
        )}
      </div>
    ) : (
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {jobs.map(job => {
          const appliedInfo = lookupApplied(job.id, job.title, job.company);
          return (
          <JobCard
            key={job.id}
            job={job}
            isSaved={savedKeys.has(`${job.source}:${job.external_job_id ?? job.id}`)}
            onToggleSave={onToggleSave}
            onApply={onApply}
            onView={onView}
            onCardClick={onCardClick}
            onExplainMatch={onExplainMatch}
            isOwner={isOwner}
            onTakedown={onTakedown}
            statusBadge={appliedInfo ? <JobWorkflowStatusBadge info={appliedInfo} /> : undefined}
          />
        );})}
      </div>
    )}
  </section>
  );
};

// ── Match score badge helpers ─────────────────────────────────────────────────
function scoreColor(score: number): { bg: string; fg: string; ring: string } {
  if (score >= 75) return { bg: 'var(--ds-ok-bg)',    fg: 'var(--ds-ok-text)',    ring: 'var(--ds-ok-border)' };
  if (score >= 55) return { bg: 'var(--ds-warn-bg)',  fg: 'var(--ds-warn-text)',  ring: 'var(--ds-warn-border)' };
  if (score >= 35) return { bg: 'var(--ds-brand-soft)',  fg: 'var(--ds-brand)', ring: 'var(--ds-brand-border)' };
  return           { bg: 'var(--ds-bg-tinted)',          fg: 'var(--ds-text-muted)', ring: 'transparent' };
}

function scoreLabel(score: number, t: (key: string, opts?: { defaultValue?: string }) => string): string {
  if (score >= 75) return t('dashboard.jobs.jobDrawer.bandStrongFit', 'Strong fit');
  if (score >= 55) return t('dashboard.jobs.jobDrawer.bandGoodFit', 'Good fit');
  if (score >= 35) return t('dashboard.jobs.jobDrawer.bandPartialFit', 'Partial fit');
  return t('dashboard.jobs.matchBandExploring', 'Exploring');
}

interface RecommendedCardProps {
  jobs:            Job[];
  totalCount:      number;
  savedKeys:       Set<string>;
  onCardClick:     (job: Job) => void;
  onToggleSave:    (job: Job) => void;
  isSignedIn:      boolean;
  hasSkillContext:  boolean;
  profileSkills:   string[];
  isRanking:       boolean;
  onRefresh:       () => void;
}

const RecommendedCard: React.FC<RecommendedCardProps> = ({
  jobs,
  totalCount,
  savedKeys,
  onCardClick,
  onToggleSave,
  isSignedIn,
  hasSkillContext,
  profileSkills,
  isRanking,
  onRefresh,
}) => {
  const { t, i18n } = useTranslation();
  const rankedCount = jobs.filter(j => typeof j.matchScore === 'number' && (j.matchScore as number) > 0).length;

  return (
    <div key={i18n.language} className="ds-card-sm flex flex-col gap-0 min-w-0 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between gap-2 pb-3">
        <div className="flex items-center gap-2.5 min-w-0">
          <div className="ds-icon-square ds-icon-square-lg shrink-0">
            <Sparkles className="w-5 h-5" />
          </div>
          <div className="min-w-0">
            <h3 className="t-card-title">
              {t('dashboard.jobs.recommendedFor', 'Recommended for you')}
            </h3>
            <p className="t-meta">
              {hasSkillContext
                ? t('dashboard.jobs.recommended.skillMatched', {
                    defaultValue: '{{ranked}} of {{total}} skill-matched · {{skills}} skills detected',
                    ranked: rankedCount,
                    total: totalCount,
                    skills: profileSkills.length,
                  })
                : t('dashboard.jobs.recommended.addSkillsPersonalise', {
                    defaultValue: '{{total}} total · add skills to personalise',
                    total: totalCount,
                  })}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {isRanking && (
            <LoadingSpinner size="xs" showText={false} />
          )}
          <button
            type="button"
            onClick={onRefresh}
            title={t('dashboard.jobs.recommended.refreshTitle', 'Refresh recommendations')}
            className="ds-btn ds-btn-ghost ds-btn-sm"
          >
            <RefreshCw className="w-3.5 h-3.5" />
          </button>
          {totalCount > jobs.length && (
            <span className="ds-chip t-num">
              +{totalCount - jobs.length}
            </span>
          )}
        </div>
      </div>

      {/* No skill context nudge */}
      {isSignedIn && !hasSkillContext && (
        <div className="ds-recessed mb-3 flex items-start gap-2.5"
          style={{ background: 'var(--ds-brand-soft)', borderColor: 'var(--ds-brand-border)' }}>
          <Zap className="w-4 h-4 mt-0.5 shrink-0" style={{ color: 'var(--ds-brand)' }} />
          <div>
            <p className="t-card-title">{t('dashboard.jobs.recommended.unlockScoresTitle', 'Add your skills to unlock match scores')}</p>
            <p className="t-meta">{t('dashboard.jobs.recommended.unlockScoresHint', 'Go to Settings → Profile → Work and fill in your skills to see % match for each job.')}</p>
          </div>
        </div>
      )}

      {/* Job rows */}
      {jobs.length === 0 ? (
        <div className="flex flex-col items-center gap-2 py-10 px-4 text-center">
          <Briefcase className="w-10 h-10 opacity-30" style={{ color: 'var(--ds-text-muted)' }} />
          <p className="t-card-title">{t('dashboard.jobs.recommended.noMatches', 'No matches yet')}</p>
          <p className="t-meta">{t('dashboard.jobs.recommended.noMatchesHint', 'Adjust filters or check back after the next job ingest (every 6 h).')}</p>
        </div>
      ) : (
        <ul className="flex flex-col">
          {jobs.map((job, idx) => {
            const key = `${job.source}:${job.external_job_id ?? job.id}`;
            const isSaved = savedKeys.has(key);
            const score = typeof job.matchScore === 'number' ? Math.round(job.matchScore) : null;
            const colors = score !== null ? scoreColor(score) : null;
            const matched: string[] = (job as { matchedSkills?: string[] }).matchedSkills?.slice(0, 3) ?? [];
            const missing: string[] = (job as { missingSkills?: string[] }).missingSkills?.slice(0, 2) ?? [];

            return (
              <li
                key={job.id}
                className={idx > 0 ? 'border-t' : ''}
                style={{ borderColor: 'var(--ds-divider)' }}
              >
                <button
                  type="button"
                  onClick={() => onCardClick(job)}
                  className="w-full text-left flex gap-3 py-3 px-4 min-h-[60px] transition-colors touch-manipulation"
                  style={{ background: 'transparent' }}
                  onMouseEnter={e => (e.currentTarget.style.background = 'var(--ds-bg-hover)')}
                  onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                >
                  {/* Company logo or fallback */}
                  <div className="ds-icon-square ds-icon-square-lg shrink-0 mt-0.5 overflow-hidden">
                    {job.company_logo_url
                      ? <img src={job.company_logo_url} alt={`${job.company} logo`} className="w-full h-full object-contain" onError={e => { (e.target as HTMLImageElement).style.display='none'; }} />
                      : <span className="t-tag t-strong">{job.company.slice(0, 2).toUpperCase()}</span>}
                  </div>

                  <div className="flex-1 min-w-0">
                    {/* Title row */}
                    <div className="flex items-start justify-between gap-2 mb-0.5">
                      <p className="t-card-title truncate leading-tight">{job.title}</p>
                      {score !== null && colors && (
                        <span
                          className="ds-chip t-num shrink-0"
                          style={{ background: colors.bg, color: colors.fg, borderColor: colors.ring }}
                        >
                          {score}% · {scoreLabel(score, t)}
                        </span>
                      )}
                    </div>

                    {/* Company + location */}
                    <p className="t-meta truncate mb-1">
                      {job.company}
                      {job.location ? ` · ${job.location}` : ''}
                      {job.remote ? ` · ${t('dashboard.jobs.jobCard.remote', 'Remote')}` : ''}
                      {job.job_type ? ` · ${job.job_type}` : ''}
                    </p>

                    {/* Matched skills */}
                    {matched.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-1">
                        {matched.map(s => (
                          <span key={s} className="ds-chip ds-chip-ok">✓ {s}</span>
                        ))}
                        {missing.map(s => (
                          <span key={s} className="ds-chip ds-chip-warn">+ {s}</span>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Bookmark */}
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); onToggleSave(job); }}
                    aria-label={isSaved ? t('dashboard.jobs.unsaveJob', 'Unsave') : t('dashboard.jobs.jobCard.saveJob', 'Save job')}
                    className="ds-btn ds-btn-ghost ds-btn-sm shrink-0 self-start touch-manipulation"
                  >
                    <Bookmark
                      className="w-4 h-4"
                      strokeWidth={2}
                      style={{
                        color: isSaved ? 'var(--ds-brand)' : 'var(--ds-text-muted)',
                        fill:  isSaved ? 'var(--ds-brand)' : 'transparent',
                      }}
                    />
                  </button>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

interface SavedJobsCardProps {
  isSignedIn: boolean;
  savedKeys:  Set<string>;
  onSeeAll:   () => void;
}

const SavedJobsCard: React.FC<SavedJobsCardProps> = ({ isSignedIn, savedKeys, onSeeAll }) => {
  const { t } = useTranslation();
  return (
    <div className="ds-card flex flex-col gap-3 min-w-0">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <div className="ds-icon-square shrink-0">
            <Bookmark className="w-5 h-5" />
          </div>
          <div className="min-w-0">
            <h3 className="t-card-title truncate">
              {t('dashboard.jobs.savedJobs', 'Saved jobs')}
            </h3>
            <p className="t-meta truncate">
              {isSignedIn
                ? t('dashboard.jobs.savedSubtitle', { defaultValue: '{{count}} saved · revisit anytime', count: savedKeys.size })
                : t('dashboard.jobs.savedSignedOut', 'Sign in to save and revisit jobs')}
            </p>
          </div>
        </div>
        {isSignedIn && savedKeys.size > 0 && (
          <button
            type="button"
            onClick={onSeeAll}
            className="ds-btn ds-btn-link shrink-0 touch-manipulation"
          >
            {t('dashboard.jobs.seeAll', 'See all')}
            <ChevronRightIcon className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {!isSignedIn ? (
        <p className="t-meta py-2">
          {t('dashboard.jobs.savedSignedOutHint', 'Bookmark roles you like to follow up on later.')}
        </p>
      ) : savedKeys.size === 0 ? (
        <p className="t-meta py-2">
          {t('dashboard.jobs.savedEmpty', 'You haven’t saved any jobs yet. Tap the bookmark on a card to save it here.')}
        </p>
      ) : (
        <button
          type="button"
          onClick={onSeeAll}
          className="ds-btn ds-btn-secondary min-h-[44px] w-full justify-between touch-manipulation"
        >
          <span className="t-body truncate">
            {t('dashboard.jobs.openSavedTab', { defaultValue: 'Open {{count}} saved jobs', count: savedKeys.size })}
          </span>
          <ChevronRightIcon className="w-4 h-4 t-muted shrink-0" />
        </button>
      )}
    </div>
  );
};

interface ForYouTodayCardProps {
  applyReminderCount:  number;
  optimizeRecCount:    number;
  interviewPrepCount:  number;
  onReviewReminders:   () => void;
  onReviewOptimize:    () => void;
  onReviewInterview:   () => void;
}

const ForYouTodayCard: React.FC<ForYouTodayCardProps> = ({
  applyReminderCount,
  optimizeRecCount,
  interviewPrepCount,
  onReviewReminders,
  onReviewOptimize,
  onReviewInterview,
}) => {
  const { t } = useTranslation();

  const items: Array<{
    icon:    React.ComponentType<{ className?: string }>;
    title:   string;
    sub:     string;
    count:   number;
    accent:  string;
    onReview: () => void;
  }> = [
    {
      icon:    Bookmark,
      title:   t('dashboard.jobs.taskApplyReminders', 'Apply reminders'),
      sub:     t('dashboard.jobs.taskApplyRemindersSub', 'Saved jobs awaiting your apply.'),
      count:   applyReminderCount,
      accent:  'var(--ds-brand)',
      onReview: onReviewReminders,
    },
    {
      icon:    Zap,
      title:   t('dashboard.jobs.taskOptimizePrompts', 'Optimize prompts'),
      sub:     t('dashboard.jobs.taskOptimizePromptsSub', 'Roles ready for a tailored resume pass.'),
      count:   optimizeRecCount,
      accent:  'var(--ds-warn)',
      onReview: onReviewOptimize,
    },
    {
      icon:    Video,
      title:   t('dashboard.jobs.taskInterviewPrep', 'Interview prep'),
      sub:     t('dashboard.jobs.taskInterviewPrepSub', 'Practice questions matched to your top roles.'),
      count:   interviewPrepCount,
      accent:  'var(--ds-ok)',
      onReview: onReviewInterview,
    },
  ];

  return (
    <aside
      className="ds-card flex flex-col gap-2 min-w-0"
      aria-label={t('dashboard.jobs.forYouToday', 'For you today')}
    >
      <div className="flex items-center justify-between gap-2 mb-1">
        <div className="flex items-center gap-2 min-w-0">
          <div className="ds-icon-square shrink-0">
            <Sparkles className="w-5 h-5" />
          </div>
          <div className="min-w-0">
            <h3 className="t-card-title truncate">
              {t('dashboard.jobs.forYouToday', 'For you today')}
            </h3>
            <p className="t-meta truncate">
              {t('dashboard.jobs.forYouTodaySubtitle', 'Quick wins to keep momentum.')}
            </p>
          </div>
        </div>
      </div>

      <ul className="flex flex-col">
        {items.map((it) => (
          <li
            key={it.title}
            className="flex items-center gap-3 py-2.5 border-b last:border-b-0"
            style={{ borderColor: 'var(--ds-divider)' }}
          >
            <div className="ds-icon-square shrink-0" style={{ color: it.accent }}>
              <it.icon className="w-5 h-5" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="t-card-title truncate">{it.title}</p>
              <p className="t-meta truncate">{it.sub}</p>
            </div>
            <span
              className="ds-chip t-num shrink-0"
              style={it.count > 0 ? { background: it.accent, color: 'var(--ds-text-on-brand)', borderColor: 'transparent' } : undefined}
            >
              {it.count}
            </span>
            <button
              type="button"
              onClick={it.onReview}
              className="ds-btn ds-btn-secondary ds-btn-sm shrink-0 touch-manipulation"
            >
              {t('dashboard.jobs.review', 'Review')}
              <ChevronRightIcon className="w-3.5 h-3.5" />
            </button>
          </li>
        ))}
      </ul>
    </aside>
  );
};
